import React from "react";

import type { NativeQuote } from "@/modules/dashboard/dispersions/services/paymentsQuotes.service";
import { quoteFeeView } from "@/modules/dashboard/dispersions/services/paymentsQuotes.service";
import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { useTaxLabel } from "@/shared/hooks/useTaxLabel";
import { cn } from "@/shared/lib/utils";

/**
 * Copy is passed in rather than read from a namespace: the same rows mean different things to different
 * callers — a dispersion says "the beneficiary receives", a swap says "you receive" — and a shared component
 * cannot guess which namespace owns the strings.
 */
export interface QuoteBreakdownLabels {
  send: string;
  conversionFee: string;
  rate: string;
  converted: string;
  serviceFee: string;
  totalDebit: string;
  receives: string;
}

export interface QuoteBreakdownProps {
  quote: NativeQuote;
  labels: QuoteBreakdownLabels;
  /** Suffix on the dispersion-fee row, e.g. the rail name. */
  serviceFeeSuffix?: string;
  /** `card` draws its own border (step 1, swaps); `rows` inlines into a card. */
  variant?: "card" | "rows";
  /** Header slot — the caller owns the countdown, so its 1 s tick stays outside. */
  header?: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
}

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex justify-between text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium tabular-nums">{children}</span>
  </div>
);

/**
 * The money trail of a quote, in the order the money moves, so the arithmetic reads top to bottom and the
 * totals visibly add up:
 *
 * Send (origin) − conversion fee (origin) → converted (dest) − commission (dest)
 *
 * The two fees are rendered in their OWN currency and never summed. Amounts are decimal strings straight from
 * the API — no `Number` round-trip, and no fixed decimal count on the rate, which would be wrong for pairs at
 * either extreme.
 *
 * Same-currency quotes hide the conversion, rate and converted rows, which leaves exactly the card that
 * shipped before cross-currency.
 */
export const QuoteBreakdown = React.memo(function QuoteBreakdown({
  quote,
  labels,
  serviceFeeSuffix,
  variant = "card",
  header,
  footer,
  className,
}: QuoteBreakdownProps) {
  const taxLabel = useTaxLabel();
  const view = quoteFeeView(quote);

  const rows = (
    <>
      <Row label={labels.send}>
        <ColocaAmountDisplay
          value={quote.amounts.send}
          currency={quote.currency.from}
          minimumFractionDigits={2}
          maximumFractionDigits={8}
        />
      </Row>

      {view.conversion && (
        <Row label={labels.conversionFee}>
          <ColocaAmountDisplay
            value={view.conversion.total}
            currency={view.conversion.currency}
            minimumFractionDigits={2}
            maximumFractionDigits={8}
          />
        </Row>
      )}

      {view.isCrossCurrency && (
        <Row label={labels.rate}>
          <span className="inline-flex items-center gap-1">
            1 {quote.currency.from} ={" "}
            <ColocaAmountDisplay
              value={quote.rate.effective}
              currency={quote.currency.to}
              minimumFractionDigits={2}
              maximumFractionDigits={8}
            />
          </span>
        </Row>
      )}

      {view.converted && (
        <Row label={labels.converted}>
          <ColocaAmountDisplay
            value={view.converted}
            currency={quote.currency.to}
            minimumFractionDigits={2}
            maximumFractionDigits={8}
          />
        </Row>
      )}

      {view.payout && (
        <Row label={serviceFeeSuffix ? `${labels.serviceFee} · ${serviceFeeSuffix}` : labels.serviceFee}>
          <ColocaAmountDisplay
            value={view.payout.total}
            currency={view.payout.currency}
            minimumFractionDigits={2}
            maximumFractionDigits={8}
          />
        </Row>
      )}

      {view.taxes.map((tax) => (
        <Row key={tax.code} label={taxLabel(tax)}>
          <ColocaAmountDisplay
            value={tax.amount}
            currency={quote.currency.to}
            minimumFractionDigits={2}
            maximumFractionDigits={8}
          />
        </Row>
      ))}

      {/* `send` already includes the conversion fee, so it IS the total debit. */}
      <Row label={labels.totalDebit}>
        <ColocaAmountDisplay
          value={quote.amounts.send}
          currency={quote.currency.from}
          minimumFractionDigits={2}
          maximumFractionDigits={8}
        />
      </Row>

      <div className="h-px bg-border" />
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-semibold text-foreground">{labels.receives}</span>
        <span className="text-xl font-bold tabular-nums text-primary">
          <ColocaAmountDisplay
            value={view.beneficiaryReceives}
            currency={quote.currency.to}
            minimumFractionDigits={2}
            maximumFractionDigits={8}
          />
        </span>
      </div>
    </>
  );

  if (variant === "rows") {
    return <div className={cn("flex flex-col gap-2.5", className)}>{rows}</div>;
  }

  return (
    <div className={cn("overflow-hidden rounded-2xl border border-border", className)}>
      {header}
      <div className="flex flex-col gap-2.5 px-5 py-4">{rows}</div>
      {footer}
    </div>
  );
});
